import { 
  Object3D, 
  Vector3, 
  Mesh, 
  MeshStandardMaterial, 
  SphereGeometry,
  OctahedronGeometry,
  BoxGeometry,
  PlaneGeometry,
  Color,
  AnimationMixer,
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Clock,
  AmbientLight,
  DirectionalLight,
  HemisphereLight,
  PointLight,
  PCFSoftShadowMap,
  SRGBColorSpace,
  ACESFilmicToneMapping,
  Box3,
  MathUtils,
  Plane,
  PMREMGenerator,
  Raycaster,
  Vector2,
  Group,
  CylinderGeometry,
  RingGeometry,
  DoubleSide
} from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { Howl } from "howler";
import { HandTrackingService } from "../scripts/HandTrackingService";

// ============================================
// MAIN GAME CLASS - Cute cartoon baby game
// ============================================
export class GimmeMyCakeGame {
  // Three.js core
  private scene: Scene;
  private camera: PerspectiveCamera;
  private renderer: WebGLRenderer;
  private clock: Clock;
  
  // Game state
  private score: number = 0;
  private cryMeter: number = 0.5;
  private gameState: "menu" | "playing" | "gameover" = "menu";
  private touchMode: boolean = false;
  
  // Baby
  private babyModel?: Object3D;
  private babyMixer?: AnimationMixer;
  private babyBasePosition?: Vector3;
  private mouthMesh?: Mesh;
  private mouthOpen: boolean = false;
  private mouthTimer: number = 0;
  private mouthTarget?: Object3D;
  private mouthOffsetRoot?: Object3D;
  private mouthLocalOffset?: Vector3;
  private bodyLocalCenter?: Vector3;
  private bodyHitRadius: number = 0.45;
  private babyBaseScale: number = 1;
  private babyReactionTime: number = 0;
  private babyReactionDuration: number = 0;
  private babyReactionSeed: number = 0;
  
  // Cakes
  private cakes: CakeObject[] = [];
  private particles: { mesh: Mesh, velocity: Vector3, life: number }[] = [];
  private sparkleRings: { mesh: Mesh, life: number, maxLife: number }[] = [];
  private spawnTimer: number = 0;
  private heldCake?: CakeObject;
  private handPosition: Vector3 = new Vector3();
  private maxCakes: number = 6;
  // Table surface is calibrated from the actual table mesh in setupScene()
  private tableSurfaceY: number = 0.045;
  private tableSurfaceZ: number = 0.85;
  private handPlaneZ: number = 0.85;
  private readonly cakeRestOffsetY: number = 0.0475;
  private raycaster: Raycaster;
  private pointer: Vector2;
  private dragPlane: Plane;

  // Eating animation queue
  private eatingCakes: { mesh: Object3D, targetPos: Vector3, progress: number, startPos: Vector3 }[] = [];

  // Screen flash
  private flashOverlay?: HTMLDivElement;
  private flashAnimation?: Animation;
  private screenShakeAnimation?: Animation;

  // Hand tracking
  private handTracker: HandTrackingService;
  private handMarker?: Mesh;
  private wasHandPinching: boolean = false;
  // A pointer drag owns the held cake, so pinch input stays out of the way
  private pointerActive: boolean = false;

  // Audio
  private successSound?: Howl;
  private missSound?: Howl;
  private crySound?: Howl;

  constructor() {
    // Setup Three.js
    this.scene = new Scene();
    this.scene.background = new Color(0xded8d1);
    
    this.camera = new PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    this.camera.position.set(0, 1.35, 3.2);
    this.camera.lookAt(0, 0.7, 0);
    
    this.renderer = new WebGLRenderer({ antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.92;
    
    this.clock = new Clock();
    this.raycaster = new Raycaster();
    this.pointer = new Vector2();
    this.dragPlane = new Plane(new Vector3(0, 0, 1), -this.handPlaneZ);
    this.touchMode = window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;
    
    // Insert canvas as first child of body
    document.body.insertBefore(this.renderer.domElement, document.body.firstChild);
    this.renderer.domElement.style.cssText = "position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 1; touch-action: none; -webkit-user-select: none; user-select: none;";
    
    this.setupScene();
    this.setupAudio();
    this.loadBabyModel();
    this.setupUI();
    this.setupInputs();
    this.setupResize();
    this.setupFlashOverlay();
    
    // Initialize hand tracking
    this.handTracker = new HandTrackingService();
    this.createHandMarker();

    // Start game loop
    this.animate();

    console.log("🎂 Gimme My Cake - Initialized!");
  }

  private setupScene() {
    // Warm kitchen lighting
    const ambient = new AmbientLight(0xfff6eb, 0.32);
    this.scene.add(ambient);
    
    const hemi = new HemisphereLight(0xcfe0f3, 0xbfa48f, 0.35);
    hemi.position.set(0, 10, 0);
    this.scene.add(hemi);
    
    // Main sunlight from window
    const sunlight = new DirectionalLight(0xffefdc, 1.6);
    sunlight.position.set(4.5, 7, 3);
    sunlight.castShadow = true;
    sunlight.shadow.mapSize.width = 2048;
    sunlight.shadow.mapSize.height = 2048;
    sunlight.shadow.bias = -0.0003;
    sunlight.shadow.normalBias = 0.02;
    this.scene.add(sunlight);

    // Soft fill light
    const fill = new DirectionalLight(0xc7d8f2, 0.5);
    fill.position.set(-4, 3, 2);
    this.scene.add(fill);

    const rim = new DirectionalLight(0xffffff, 0.4);
    rim.position.set(-3, 4, -4);
    this.scene.add(rim);

    const pendantLight = new PointLight(0xffdeb1, 1.4, 10, 2);
    pendantLight.position.set(0, 4.2, 0.3);
    this.scene.add(pendantLight);

    // Environment for reflections
    const pmrem = new PMREMGenerator(this.renderer);
    const env = new RoomEnvironment();
    this.scene.environment = pmrem.fromScene(env, 0.04).texture;
    env.dispose();
    pmrem.dispose();

    // === KITCHEN MATERIALS ===
    const wallMat = new MeshStandardMaterial({ 
      color: 0xe4ddd4, // Soft warm walls
      roughness: 0.95 
    });
    
    const floorMat = new MeshStandardMaterial({ 
      color: 0xc9ad8f, // Warm oak floor
      roughness: 0.75 
    });
    
    const cabinetMat = new MeshStandardMaterial({ 
      color: 0xe2ddd6, // Warm light cabinets
      roughness: 0.6,
      metalness: 0.02
    });

    const cabinetDoorMat = new MeshStandardMaterial({ 
      color: 0xd2cbc1, // Subtle door contrast
      roughness: 0.62,
      metalness: 0.02
    });
    
    const counterMat = new MeshStandardMaterial({ 
      color: 0xc9c2b8, // Warm quartz counter
      roughness: 0.35,
      metalness: 0.05
    });

    const backsplashMat = new MeshStandardMaterial({ 
      color: 0xe3ded6,
      roughness: 0.45,
      metalness: 0.04
    });

    const baseboardMat = new MeshStandardMaterial({ 
      color: 0xcfc8c0,
      roughness: 0.65 
    });
    
    const windowFrameMat = new MeshStandardMaterial({ 
      color: 0xddd8d1,
      roughness: 0.45
    });
    
    const windowGlassMat = new MeshStandardMaterial({ 
      color: 0x8fbde4, // Cool sky tint
      roughness: 0.08,
      metalness: 0.12,
      transparent: true,
      opacity: 0.22
    });

    const metalMat = new MeshStandardMaterial({ 
      color: 0xb1b1b1, 
      metalness: 0.7, 
      roughness: 0.25 
    });

    const applianceMat = new MeshStandardMaterial({ 
      color: 0xcfd3d6,
      roughness: 0.35,
      metalness: 0.45
    });

    const accentMat = new MeshStandardMaterial({ 
      color: 0xaeb8bf,
      roughness: 0.65,
      metalness: 0.05
    });

    const toeKickMat = new MeshStandardMaterial({ 
      color: 0x2f2f2f,
      roughness: 0.7
    });

    // === FLOOR ===
    const floor = new Mesh(new PlaneGeometry(12, 10), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, -0.5, 0);
    floor.receiveShadow = true;
    this.scene.add(floor);

    const runner = new Mesh(new PlaneGeometry(3.4, 1.2), accentMat);
    runner.rotation.x = -Math.PI / 2;
    runner.position.set(0, -0.49, -1.2);
    runner.receiveShadow = true;
    this.scene.add(runner);

    // === BACK WALL ===
    const backWall = new Mesh(new PlaneGeometry(12, 6), wallMat);
    backWall.position.set(0, 2.5, -3);
    backWall.receiveShadow = true;
    this.scene.add(backWall);

    // === LEFT WALL ===
    const leftWall = new Mesh(new PlaneGeometry(10, 6), wallMat);
    leftWall.rotation.y = Math.PI / 2;
    leftWall.position.set(-6, 2.5, 0);
    leftWall.receiveShadow = true;
    this.scene.add(leftWall);

    // === RIGHT WALL (with window) ===
    const rightWall = new Mesh(new PlaneGeometry(10, 6), wallMat);
    rightWall.rotation.y = -Math.PI / 2;
    rightWall.position.set(6, 2.5, 0);
    rightWall.receiveShadow = true;
    this.scene.add(rightWall);

    // Baseboards
    const baseboardHeight = 0.12;
    const baseboardDepth = 0.08;
    const backBaseboard = new Mesh(new BoxGeometry(12, baseboardHeight, baseboardDepth), baseboardMat);
    backBaseboard.position.set(0, -0.5 + baseboardHeight / 2, -2.95);
    this.scene.add(backBaseboard);

    const leftBaseboard = new Mesh(new BoxGeometry(baseboardDepth, baseboardHeight, 10), baseboardMat);
    leftBaseboard.position.set(-5.96, -0.5 + baseboardHeight / 2, 0);
    this.scene.add(leftBaseboard);

    const rightBaseboard = new Mesh(new BoxGeometry(baseboardDepth, baseboardHeight, 10), baseboardMat);
    rightBaseboard.position.set(5.96, -0.5 + baseboardHeight / 2, 0);
    this.scene.add(rightBaseboard);

    // === WINDOW ===
    const windowGroup = new Object3D();
    windowGroup.position.set(5.95, 2.5, 0);
    windowGroup.rotation.y = -Math.PI / 2;
    
    // Window frame
    const windowFrame = new Mesh(new BoxGeometry(2.5, 2, 0.1), windowFrameMat);
    windowGroup.add(windowFrame);
    
    // Window glass
    const windowGlass = new Mesh(new PlaneGeometry(2.2, 1.7), windowGlassMat);
    windowGlass.position.z = 0.06;
    windowGroup.add(windowGlass);
    
    // Window divider (cross)
    const dividerV = new Mesh(new BoxGeometry(0.05, 1.7, 0.12), windowFrameMat);
    dividerV.position.z = 0.06;
    windowGroup.add(dividerV);
    
    const dividerH = new Mesh(new BoxGeometry(2.2, 0.05, 0.12), windowFrameMat);
    dividerH.position.z = 0.06;
    windowGroup.add(dividerH);

    const windowSill = new Mesh(new BoxGeometry(2.6, 0.12, 0.3), windowFrameMat);
    windowSill.position.set(0, -1.05, 0.12);
    windowGroup.add(windowSill);
    
    this.scene.add(windowGroup);

    // === KITCHEN COUNTER (back) ===
    const counterBase = new Mesh(new BoxGeometry(8, 1.4, 0.8), cabinetMat);
    counterBase.position.set(0, 0.2, -2.5);
    counterBase.castShadow = true;
    counterBase.receiveShadow = true;
    this.scene.add(counterBase);

    const toeKick = new Mesh(new BoxGeometry(7.6, 0.22, 0.6), toeKickMat);
    toeKick.position.set(0, -0.39, -2.3);
    toeKick.receiveShadow = true;
    this.scene.add(toeKick);

    const doorGeom = new BoxGeometry(1.7, 1.05, 0.03);
    const handleGeom = new BoxGeometry(0.35, 0.03, 0.05);
    const doorXs = [-2.6, -0.9, 0.9, 2.6];
    doorXs.forEach((x, i) => {
      const door = new Mesh(doorGeom, cabinetDoorMat);
      door.position.set(x, 0.25, -2.08);
      door.castShadow = true;
      this.scene.add(door);

      const handleOffset = i < 2 ? 0.55 : -0.55;
      const handle = new Mesh(handleGeom, metalMat);
      handle.position.set(x + handleOffset, 0.45, -2.06);
      this.scene.add(handle);
    });
    
    const counterTop = new Mesh(new BoxGeometry(8.2, 0.08, 0.95), counterMat);
    counterTop.position.set(0, 1.02, -2.5);
    counterTop.castShadow = true;
    counterTop.receiveShadow = true;
    this.scene.add(counterTop);

    const backsplash = new Mesh(new PlaneGeometry(7.6, 1.2), backsplashMat);
    backsplash.position.set(0, 1.62, -2.58);
    backsplash.receiveShadow = true;
    this.scene.add(backsplash);

    // === UPPER CABINETS ===
    const upperCabinetGeom = new BoxGeometry(2.2, 1.1, 0.38);
    const upperCabinetXs = [-2.6, 0, 2.6];
    upperCabinetXs.forEach((x, i) => {
      const upper = new Mesh(upperCabinetGeom, cabinetMat);
      upper.position.set(x, 3, -2.72);
      upper.castShadow = true;
      this.scene.add(upper);

      const handleOffset = i < 2 ? 0.75 : -0.75;
      const upperHandle = new Mesh(new BoxGeometry(0.28, 0.03, 0.05), metalMat);
      upperHandle.position.set(x + handleOffset, 2.85, -2.5);
      this.scene.add(upperHandle);
    });

    const shelf = new Mesh(new BoxGeometry(4.8, 0.06, 0.28), cabinetDoorMat);
    shelf.position.set(0, 2.15, -2.78);
    shelf.castShadow = true;
    this.scene.add(shelf);

    const bowl1 = new Mesh(new SphereGeometry(0.08, 12, 12), accentMat);
    bowl1.position.set(-0.6, 2.25, -2.7);
    this.scene.add(bowl1);

    const bowl2 = new Mesh(new SphereGeometry(0.09, 12, 12), accentMat);
    bowl2.position.set(0.3, 2.25, -2.72);
    this.scene.add(bowl2);

    // === REFRIGERATOR (left side) ===
    const fridge = new Mesh(new BoxGeometry(1.2, 3, 0.9), applianceMat);
    fridge.position.set(-4.8, 1, -2.2);
    fridge.castShadow = true;
    this.scene.add(fridge);
    
    // Fridge handle
    const fridgeHandle = new Mesh(new BoxGeometry(0.05, 0.8, 0.05), metalMat);
    fridgeHandle.position.set(-4.15, 1.3, -2.2);
    this.scene.add(fridgeHandle);

    const fridgeDivider = new Mesh(new BoxGeometry(1.18, 0.02, 0.88), metalMat);
    fridgeDivider.position.set(-4.8, 1.75, -2.2);
    this.scene.add(fridgeDivider);

    // === HIGHCHAIR TABLE (where baby sits) ===
    // The baby's highchair is the model itself, just add a simple table nearby
    const tableMat = new MeshStandardMaterial({ 
      color: 0xd2b9a4, 
      roughness: 0.55 
    });
    const table = new Mesh(new BoxGeometry(1.7, 0.05, 1.1), tableMat);
    table.position.set(0, 0.02, 0.85);
    table.castShadow = true;
    table.receiveShadow = true;
    this.scene.add(table);

    // Calibrate interaction plane + cake spawn height from the actual table geometry
    this.tableSurfaceY = table.position.y + 0.05 / 2;
    this.tableSurfaceZ = table.position.z;
    this.handPlaneZ = this.tableSurfaceZ;
    this.dragPlane.constant = -this.handPlaneZ;
    
    // Table legs
    const legMat = new MeshStandardMaterial({ color: 0x3d3d3d, metalness: 0.6, roughness: 0.3 });
    const legGeom = new BoxGeometry(0.05, 0.48, 0.05);
    const positions = [
      [-0.75, -0.22, 0.35], [0.75, -0.22, 0.35],
      [-0.75, -0.22, 1.25], [0.75, -0.22, 1.25]
    ];
    positions.forEach(pos => {
      const leg = new Mesh(legGeom, legMat);
      leg.position.set(pos[0], pos[1], pos[2]);
      leg.castShadow = true;
      this.scene.add(leg);
    });

    // === DECORATIVE PLANT ===
    const potMat = new MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.85 });
    const pot = new Mesh(new CylinderGeometry(0.14, 0.18, 0.26, 16), potMat);
    pot.position.set(3.6, 1.15, -2.4);
    this.scene.add(pot);
    
    const plantMat = new MeshStandardMaterial({ color: 0x3f6f52, roughness: 0.8 });
    const plant = new Mesh(new SphereGeometry(0.18, 12, 12), plantMat);
    plant.position.set(3.6, 1.4, -2.4);
    plant.scale.set(1, 1.2, 1);
    this.scene.add(plant);

    const plant2 = new Mesh(new SphereGeometry(0.16, 12, 12), plantMat);
    plant2.position.set(3.48, 1.32, -2.35);
    this.scene.add(plant2);

    // === CEILING LIGHT ===
    const lightFixtureMat = new MeshStandardMaterial({ 
      color: 0xf6f3ee, 
      emissive: 0xfff1d6,
      emissiveIntensity: 0.2,
      roughness: 0.35,
      metalness: 0.1
    });
    const lightFixture = new Mesh(new CylinderGeometry(0.32, 0.22, 0.35, 24), lightFixtureMat);
    lightFixture.position.set(0, 4.2, 0.3);
    lightFixture.castShadow = true;
    this.scene.add(lightFixture);

    const pendantCap = new Mesh(new CylinderGeometry(0.12, 0.12, 0.08, 16), metalMat);
    pendantCap.position.set(0, 4.38, 0.3);
    this.scene.add(pendantCap);
    
    // Pendant cord
    const cord = new Mesh(new BoxGeometry(0.02, 0.7, 0.02), new MeshStandardMaterial({ color: 0x333333 }));
    cord.position.set(0, 4.65, 0.3);
    this.scene.add(cord);

    console.log("🏠 Modern kitchen created!");
  }

  private setupAudio() {
    // Audio files are optional - game works without them
    this.successSound = new Howl({
      src: ['/audio/success.mp3', '/audio/success.wav'],
      volume: 0.5,
      onloaderror: () => console.log("No success sound found - that's ok!")
    });
    
    this.missSound = new Howl({
      src: ['/audio/miss.mp3', '/audio/miss.wav'],
      volume: 0.3,
      onloaderror: () => console.log("No miss sound found - that's ok!")
    });
    
    this.crySound = new Howl({
      src: ['/audio/cry.mp3', '/audio/cry.wav'],
      volume: 0.4,
      loop: true,
      onloaderror: () => console.log("No cry sound found - that's ok!")
    });
  }

  private async loadBabyModel() {
    const loader = new GLTFLoader();
    
    try {
      const gltf = await loader.loadAsync('/baby.glb');
      this.babyModel = gltf.scene;
      
      // Calculate bounding box to center and scale properly
      const box = new Box3().setFromObject(this.babyModel);
      const size = box.getSize(new Vector3());
      const center = box.getCenter(new Vector3());
      
      // Scale to reasonable size (target height ~1.5 units)
      const targetHeight = 1.5;
      const scale = targetHeight / size.y;
      this.babyModel.scale.setScalar(scale);
      this.babyBaseScale = scale;
      
      // Center and position on table
      this.babyModel.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
      this.babyModel.position.y += 0.6;
      this.babyBasePosition = this.babyModel.position.clone();
      
      // Enable shadows
      this.babyModel.traverse((child) => {
        if ((child as Mesh).isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      
      this.scene.add(this.babyModel);
      this.babyModel.updateWorldMatrix(true, true);
      this.cacheMouthTarget();
      
      // Setup animations if available
      if (gltf.animations.length > 0) {
        this.babyMixer = new AnimationMixer(this.babyModel);
        const action = this.babyMixer.clipAction(gltf.animations[0]);
        action.play();
        console.log(`🍼 Baby loaded with ${gltf.animations.length} animations!`);
      } else {
        console.log("🍼 Cute baby loaded!");
      }
    } catch (error) {
      console.error("Failed to load baby model, using fallback:", error);
      this.createFallbackBaby();
    }
  }

  private createFallbackBaby() {
    // Simple fallback baby if GLB fails to load
    this.babyModel = new Object3D();
    
    const skinMaterial = new MeshStandardMaterial({ color: 0xFFDBC4, roughness: 0.8 });
    const clothMaterial = new MeshStandardMaterial({ color: 0x87CEEB, roughness: 0.9 });
    
    // Body
    const body = new Mesh(new SphereGeometry(0.25, 32, 32), clothMaterial);
    body.scale.set(1, 1.2, 0.9);
    body.position.set(0, 0.3, 0);
    this.babyModel.add(body);
    
    // Head
    const head = new Mesh(new SphereGeometry(0.22, 32, 32), skinMaterial);
    head.position.set(0, 0.7, 0);
    this.babyModel.add(head);
    
    // Eyes
    const eyeMaterial = new MeshStandardMaterial({ color: 0x4a3728 });
    const leftEye = new Mesh(new SphereGeometry(0.04, 16, 16), eyeMaterial);
    leftEye.position.set(-0.08, 0.73, 0.18);
    this.babyModel.add(leftEye);
    
    const rightEye = new Mesh(new SphereGeometry(0.04, 16, 16), eyeMaterial);
    rightEye.position.set(0.08, 0.73, 0.18);
    this.babyModel.add(rightEye);
    
    // Mouth
    const mouthMaterial = new MeshStandardMaterial({ color: 0xFF6B6B });
    this.mouthMesh = new Mesh(new SphereGeometry(0.04, 16, 16), mouthMaterial);
    this.mouthMesh.position.set(0, 0.6, 0.2);
    this.babyModel.add(this.mouthMesh);
    
    this.babyModel.position.set(0, 0.1, 0);
    this.babyModel.scale.setScalar(1.8);
    this.babyBaseScale = 1.8;
    this.babyBasePosition = this.babyModel.position.clone();
    this.scene.add(this.babyModel);
    this.babyModel.updateWorldMatrix(true, true);
    this.cacheMouthTarget();
    
    console.log("🍼 Fallback baby created!");
  }

  private cacheMouthTarget() {
    if (!this.babyModel) return;

    const bounds = new Box3().setFromObject(this.babyModel);
    const size = bounds.getSize(new Vector3());
    const center = bounds.getCenter(new Vector3());

    // The messy-splat sphere follows the model bounds, not the model pivot,
    // which can sit well outside the visible baby.
    this.bodyLocalCenter = this.babyModel.worldToLocal(center.clone());
    this.bodyHitRadius = Math.max(0.3, size.y * 0.28);

    let mouth: Object3D | undefined = this.mouthMesh;
    let jaw: Object3D | undefined;
    let head: Object3D | undefined;

    this.babyModel.traverse((child) => {
      const name = child.name.toLowerCase();
      if (!mouth && (name.includes("mouth") || name.includes("lip"))) mouth = child;
      if (!jaw && name.includes("jaw")) jaw = child;
      if (!head && (name.includes("head") || name.includes("face"))) head = child;
    });

    // A named node is only trustworthy when it actually sits on the upper body.
    const named = mouth || jaw;
    if (named) {
      const namedWorld = named.getWorldPosition(new Vector3());
      this.mouthTarget = namedWorld.y > center.y && bounds.containsPoint(namedWorld) ? named : undefined;
    } else {
      this.mouthTarget = undefined;
    }

    const offsetRoot = head || this.babyModel;
    const worldMouth = this.findFaceMouth(bounds, size, center) ?? (() => {
      const headBounds = head ? new Box3().setFromObject(head) : bounds;
      const headSize = headBounds.getSize(new Vector3());
      const headCenter = headBounds.getCenter(new Vector3());
      const heightRatio = head ? 0.35 : 0.74;
      const depthRatio = head ? 0.45 : 0.2;
      return new Vector3(
        headCenter.x,
        headBounds.min.y + headSize.y * heightRatio,
        headCenter.z + headSize.z * depthRatio
      );
    })();

    this.mouthOffsetRoot = offsetRoot;
    this.mouthLocalOffset = offsetRoot.worldToLocal(worldMouth.clone());

    console.log(
      `👄 Mouth target at ${worldMouth.x.toFixed(2)}, ${worldMouth.y.toFixed(2)}, ${worldMouth.z.toFixed(2)}`
    );
  }

  /**
   * Finds the mouth by measuring the model, because this baby model is a single
   * unnamed mesh with no mouth, jaw or head node to read.
   *
   * Horizontal rays trace the front surface down the model's centre line. The
   * nose is the most forward point of the head and the chin the most forward
   * point below it, so the open mouth is the recess between the two.
   */
  private findFaceMouth(bounds: Box3, size: Vector3, center: Vector3): Vector3 | null {
    if (!this.babyModel) return null;

    const probe = new Raycaster();
    const direction = new Vector3(0, 0, -1);
    const origin = new Vector3();
    const start = bounds.max.z + Math.max(1, size.z);
    const samples = 60;

    const profile: { y: number; z: number }[] = [];
    for (let i = 0; i <= samples; i++) {
      const y = bounds.max.y - (size.y * 0.5 * i) / samples;
      origin.set(center.x, y, start);
      probe.set(origin, direction);
      const hit = probe.intersectObject(this.babyModel, true)[0];
      if (hit) profile.push({ y, z: hit.point.z });
    }
    if (profile.length < 8) return null;

    const noseLimit = bounds.max.y - size.y * 0.3;
    let nose = -1;
    for (let i = 0; i < profile.length; i++) {
      if (profile[i].y < noseLimit) break;
      if (nose === -1 || profile[i].z > profile[nose].z) nose = i;
    }
    if (nose === -1) return null;

    let chin = -1;
    for (let i = nose + 1; i < profile.length; i++) {
      if (profile[i].y < profile[nose].y - size.y * 0.18) break;
      if (chin === -1 || profile[i].z > profile[chin].z) chin = i;
    }
    if (chin <= nose + 1) return null;

    let mouth = nose + 1;
    for (let i = nose + 1; i < chin; i++) {
      if (profile[i].z < profile[mouth].z) mouth = i;
    }

    return new Vector3(center.x, profile[mouth].y, profile[mouth].z);
  }

  private getBodyCenter(): Vector3 {
    if (this.babyModel && this.bodyLocalCenter) {
      return this.babyModel.localToWorld(this.bodyLocalCenter.clone());
    }
    return this.babyModel?.position.clone() ?? new Vector3();
  }

  private setupUI() {
    const uiRoot = document.getElementById('ui-root') || document.createElement('div');
    uiRoot.id = 'ui-root';
    uiRoot.innerHTML = `
      <div id="game-ui" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 100; pointer-events: none; padding: calc(env(safe-area-inset-top) + 8px) calc(env(safe-area-inset-right) + 12px) calc(env(safe-area-inset-bottom) + 12px) calc(env(safe-area-inset-left) + 12px); box-sizing: border-box;">
        <div id="score" style="text-align: center; padding: clamp(10px, 2.4vw, 18px) clamp(16px, 4vw, 24px); font-size: clamp(16px, 4vw, 24px); color: white; text-shadow: 0 2px 8px rgba(0,0,0,0.5); display: none;">
          Score: 0
        </div>
        <div id="cry-meter-container" style="display: none; width: min(220px, 70vw); height: clamp(12px, 2.5vw, 20px); background: #333; margin: 8px auto 0; border-radius: 12px; overflow: hidden;">
          <div id="cry-fill" style="width: 50%; height: 100%; background: linear-gradient(90deg, #4CAF50, #FFC107, #f44336); transition: width 0.3s;"></div>
        </div>
      </div>
      <div id="menu-screen" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 18px; background: rgba(0,0,0,0.7); z-index: 200; pointer-events: auto; padding: calc(env(safe-area-inset-top) + 24px) 16px calc(env(safe-area-inset-bottom) + 24px); text-align: center;">
        <h1 style="color: white; font-size: clamp(30px, 7vw, 52px); margin-bottom: 4px; text-shadow: 3px 3px 6px rgba(0,0,0,0.5);">🎂 Gimme My Cake!</h1>
        <p style="color: #ccc; margin-bottom: 8px; font-size: clamp(16px, 4.2vw, 20px); max-width: min(360px, 85vw); line-height: 1.4;">Drag or flick cakes to feed the crying baby!</p>
        <button id="start-touch" style="padding: clamp(12px, 3vw, 16px) clamp(28px, 8vw, 48px); font-size: clamp(16px, 4.5vw, 22px); cursor: pointer; margin: 6px 0; border-radius: 26px; border: none; background: linear-gradient(135deg, #4CAF50, #45a049); color: white; box-shadow: 0 4px 15px rgba(0,0,0,0.3); transition: transform 0.2s; pointer-events: auto; width: min(260px, 80vw);">🎮 Play Game</button>
      </div>
      <div id="gameover-screen" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; display: none; flex-direction: column; align-items: center; justify-content: center; gap: 16px; background: rgba(0,0,0,0.8); z-index: 200; pointer-events: auto; padding: calc(env(safe-area-inset-top) + 24px) 16px calc(env(safe-area-inset-bottom) + 24px); text-align: center;">
        <h1 style="color: white; font-size: clamp(30px, 7vw, 52px);">💔 Game Over!</h1>
        <p id="final-score" style="color: #FFD700; font-size: clamp(20px, 5vw, 32px); margin: 10px 0 16px;">Score: 0</p>
        <button id="restart-btn" style="padding: clamp(12px, 3vw, 16px) clamp(28px, 8vw, 48px); font-size: clamp(16px, 4.5vw, 22px); cursor: pointer; margin-top: 6px; border-radius: 26px; border: none; background: linear-gradient(135deg, #4CAF50, #45a049); color: white; pointer-events: auto; width: min(260px, 80vw);">🔄 Play Again</button>
      </div>
`;
    if (!document.getElementById('ui-root')) {
      document.body.appendChild(uiRoot);
    }

    // Button handlers
    document.getElementById('start-touch')?.addEventListener('click', () => this.startGame());
    document.getElementById('restart-btn')?.addEventListener('click', () => this.startGame());
  }

  private setupInputs() {
    const canvas = this.renderer.domElement;
    
    canvas.addEventListener('pointerdown', (e) => this.onPointerDown(e));
    canvas.addEventListener('pointermove', (e) => this.onPointerMove(e));
    canvas.addEventListener('pointerup', () => this.onPointerUp());
  }

  private setupResize() {
    const resize = () => {
      const viewport = window.visualViewport;
      const width = viewport?.width ?? window.innerWidth;
      const height = viewport?.height ?? window.innerHeight;
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      document.documentElement.style.setProperty("--vh", `${height * 0.01}px`);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("orientationchange", resize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", resize);
      window.visualViewport.addEventListener("scroll", resize);
    }
  }

  private startGame() {
    this.gameState = "playing";
    this.score = 0;
    this.cryMeter = 0.5;
    this.spawnTimer = 0;
    this.heldCake = undefined;
    this.pointerActive = false;
    this.wasHandPinching = false;
    for (const cake of this.cakes) {
      cake.isHeld = false;
      cake.isThrown = false;
      cake.nearMouthTime = 0;
      cake.velocity.set(0, 0, 0);
    }
    
    // Show game UI
    document.getElementById('menu-screen')!.style.display = 'none';
    document.getElementById('gameover-screen')!.style.display = 'none';
    document.getElementById('score')!.style.display = 'block';
    document.getElementById('cry-meter-container')!.style.display = 'block';
    
    // Start hand tracking camera only when not in touch mode
    if (this.touchMode) {
      this.handTracker.enableTouchMode();
    } else {
      this.handTracker.startSelfieCamera();
    }

    if (this.cakes.length === 0) {
      for (let i = 0; i < 3; i++) {
        this.spawnCake();
      }
    }
    
    // Start crying sound
    this.crySound?.play();
    
    console.log("Game started!");
  }

  private onPointerDown(e: PointerEvent) {
    if (this.gameState !== "playing") return;

    this.pointerActive = true;
    this.updateHandPosition(e);

    if (this.heldCake) return;

    // Try to grab nearest cake
    let grabbed = false;
    for (const cake of this.cakes) {
      if (cake.isHeld || cake.isThrown) continue;
      if (cake.mesh.position.distanceTo(this.handPosition) < 0.5) {
        cake.isHeld = true;
        cake.nearMouthTime = 0;
        this.heldCake = cake;
        grabbed = true;
        break;
      }
    }

    if (!grabbed && this.cakes.length < this.maxCakes) {
      const spawned = this.createCakeAt(this.handPosition);
      spawned.isHeld = true;
      this.heldCake = spawned;
    }
  }

  private onPointerMove(e: PointerEvent) {
    if (this.gameState !== "playing" || !this.pointerActive) return;
    
    this.updateHandPosition(e);
    
    if (this.heldCake) {
      this.heldCake.mesh.position.copy(this.handPosition);
    }
  }

  private onPointerUp() {
    this.pointerActive = false;
    if (this.gameState !== "playing" || !this.heldCake) return;

    this.throwCake(this.heldCake);
  }

  private throwCake(cake: CakeObject) {
    // Throw cake towards baby mouth with a gentle arc
    const mouthPos = this.getMouthPosition();
    const start = cake.mesh.position.clone();
    const distance = start.distanceTo(mouthPos);
    const flightTime = MathUtils.clamp(distance / 5, 0.35, 0.9);
    cake.velocity.copy(this.computeThrowVelocity(start, mouthPos, flightTime));
    cake.isHeld = false;
    cake.isThrown = true;
    cake.nearMouthTime = 0;
    if (this.heldCake === cake) {
      this.heldCake = undefined;
    }
  }

  private updateHandPosition(e: PointerEvent) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.pointer.set(x, y);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.ray.intersectPlane(this.dragPlane, this.handPosition);
    if (!hit) {
      this.handPosition.set(x * 2.5, y * 1.5 + 1, this.handPlaneZ);
    }
    this.handPosition.y = Math.max(this.handPosition.y, this.tableSurfaceY + 0.04);
  }

  private getMouthPosition(): Vector3 {
    if (this.mouthTarget) {
      return this.mouthTarget.getWorldPosition(new Vector3());
    }

    if (this.mouthOffsetRoot && this.mouthLocalOffset) {
      return this.mouthOffsetRoot.localToWorld(this.mouthLocalOffset.clone());
    }

    if (this.babyModel) {
      const pos = new Vector3();
      this.babyModel.getWorldPosition(pos);
      pos.y += 0.6;
      return pos;
    }

    return new Vector3(0, 0.8, 0);
  }

  private computeThrowVelocity(start: Vector3, target: Vector3, flightTime: number): Vector3 {
    const gravity = new Vector3(0, -9.8, 0);
    const toTarget = target.clone().sub(start).divideScalar(flightTime);
    const gravityComp = gravity.multiplyScalar(0.5 * flightTime);
    return toTarget.sub(gravityComp);
  }

  private spawnCake() {
    // Spawn cakes on the table in front of the baby so user can grab them
    const tableX = (Math.random() - 0.5) * 1.0; // Spread across table width
    const tableZ = this.tableSurfaceZ - 0.25 + Math.random() * 0.5; // On the table in front
    const tableY = this.tableSurfaceY + this.cakeRestOffsetY; // Resting on table surface
    const position = new Vector3(tableX, tableY, tableZ);
    this.createCakeAt(position);
  }

  private createCakeAt(position: Vector3): CakeObject {
    const cakeGroup = new Group();
    cakeGroup.position.copy(position);
    cakeGroup.position.y = Math.max(cakeGroup.position.y, this.tableSurfaceY + this.cakeRestOffsetY);

    const baseGeometry = new CylinderGeometry(0.11, 0.12, 0.06, 20);
    const baseMaterial = new MeshStandardMaterial({ 
      color: new Color(0xf1c38d),
      roughness: 0.9
    });
    const base = new Mesh(baseGeometry, baseMaterial);
    base.castShadow = true;
    base.receiveShadow = true;
    cakeGroup.add(base);

    const creamGeometry = new CylinderGeometry(0.12, 0.12, 0.02, 20);
    const creamMaterial = new MeshStandardMaterial({ 
      color: new Color(0xfff2e5),
      roughness: 0.7
    });
    const cream = new Mesh(creamGeometry, creamMaterial);
    cream.position.y = 0.035;
    cream.castShadow = true;
    cream.receiveShadow = true;
    cakeGroup.add(cream);

    const frostingGeometry = new CylinderGeometry(0.13, 0.13, 0.03, 20);
    const frostingMaterial = new MeshStandardMaterial({ 
      color: new Color(0xf2a7b5),
      roughness: 0.55
    });
    const frosting = new Mesh(frostingGeometry, frostingMaterial);
    frosting.position.y = 0.06;
    frosting.castShadow = true;
    frosting.receiveShadow = true;
    cakeGroup.add(frosting);

    const plateGeometry = new CylinderGeometry(0.18, 0.18, 0.015, 24);
    const plateMaterial = new MeshStandardMaterial({ 
      color: new Color(0xd9d2c8),
      roughness: 0.6
    });
    const plate = new Mesh(plateGeometry, plateMaterial);
    plate.position.y = -0.04;
    plate.receiveShadow = true;
    cakeGroup.add(plate);

    const cherryGeometry = new SphereGeometry(0.03, 12, 12);
    const cherryMaterial = new MeshStandardMaterial({ 
      color: new Color(0xd64545),
      roughness: 0.4
    });
    const cherry = new Mesh(cherryGeometry, cherryMaterial);
    cherry.position.y = 0.095;
    cherry.castShadow = true;
    cakeGroup.add(cherry);

    cakeGroup.rotation.y = Math.random() * Math.PI * 2;

    this.scene.add(cakeGroup);
    const cake = new CakeObject(cakeGroup);
    this.cakes.push(cake);
    return cake;
  }

  private createConfetti(position: Vector3, color: Color, count: number = 12) {
    const geometry = new BoxGeometry(0.04, 0.02, 0.004);
    const material = new MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.35,
      roughness: 0.4,
      metalness: 0.1
    });

    for (let i = 0; i < count; i++) {
      const mesh = new Mesh(geometry, material);
      mesh.position.copy(position);

      const spread = 0.35;
      mesh.position.x += (Math.random() - 0.5) * spread;
      mesh.position.y += (Math.random() - 0.5) * spread * 0.6;
      mesh.position.z += (Math.random() - 0.5) * spread;
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      mesh.scale.setScalar(0.7 + Math.random() * 0.8);

      const velocity = new Vector3(
        (Math.random() - 0.5) * 5,
        4.5 + Math.random() * 3.5,
        (Math.random() - 0.5) * 5
      );

      this.scene.add(mesh);
      this.particles.push({ mesh, velocity, life: 1.4 + Math.random() * 0.6 });
    }
  }

  private createSplatter(position: Vector3, color: Color, count: number = 10) {
    const geometry = new BoxGeometry(0.05, 0.05, 0.05);
    const material = new MeshStandardMaterial({ color: color });

    for (let i = 0; i < count; i++) {
      const mesh = new Mesh(geometry, material);
      mesh.position.copy(position);
      
      // Random spread
      mesh.position.x += (Math.random() - 0.5) * 0.2;
      mesh.position.y += (Math.random() - 0.5) * 0.2;
      mesh.position.z += (Math.random() - 0.5) * 0.2;

      // Random velocity outward
      const velocity = new Vector3(
        (Math.random() - 0.5) * 4,
        (Math.random() - 0.5) * 4 + 2, // Upward bias
        (Math.random() - 0.5) * 4
      );

      this.scene.add(mesh);
      this.particles.push({ mesh, velocity, life: 1.0 + Math.random() * 0.5 });
    }
  }

  private updateParticles(dt: number) {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;

      if (p.life <= 0) {
        this.scene.remove(p.mesh);
        this.particles.splice(i, 1);
        continue;
      }

      p.velocity.y -= 9.8 * dt; // Gravity
      p.mesh.position.add(p.velocity.clone().multiplyScalar(dt));
      p.mesh.rotation.x += p.velocity.z * dt;
      p.mesh.rotation.y += p.velocity.x * dt;

      // Scale down as they die
      const scale = Math.max(0, p.life);
      p.mesh.scale.setScalar(scale);
    }
  }

  private setupFlashOverlay() {
    this.flashOverlay = document.createElement('div');
    this.flashOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle at center, rgba(255, 242, 170, 0.98) 0%, rgba(255, 210, 120, 0.7) 40%, rgba(255, 255, 255, 0) 72%);
      mix-blend-mode: screen;
      pointer-events: none;
      opacity: 0;
      z-index: 100;
      transform: scale(0.95);
      filter: blur(0px);
      will-change: opacity, transform;
    `;
    document.body.appendChild(this.flashOverlay);
  }

  private createHandMarker() {
    // Create a visible hand marker (glowing ring) so it can't be confused with a cake
    const geo = new RingGeometry(0.06, 0.1, 32);
    const mat = new MeshStandardMaterial({
      color: 0x00ff88,
      emissive: 0x00ff88,
      emissiveIntensity: 0.5,
      transparent: true,
      opacity: 0.6,
      side: DoubleSide
    });
    this.handMarker = new Mesh(geo, mat);
    this.handMarker.visible = false;
    // Keep the ring aligned with the screen/camera plane
    this.handMarker.rotation.set(0, 0, 0);
    this.scene.add(this.handMarker);
  }

  private updateHandTracking(dt: number) {
    // Update hand tracking service
    this.handTracker.update(dt);
    const frame = this.handTracker.frame;

    if (frame.hasHand) {
      const ndcX = MathUtils.clamp(frame.nx, 0, 1) * 2 - 1;
      const ndcY = MathUtils.clamp(frame.ny, 0, 1) * 2 - 1;
      this.pointer.set(ndcX, ndcY);
      this.raycaster.setFromCamera(this.pointer, this.camera);
      const hit = this.raycaster.ray.intersectPlane(this.dragPlane, this.handPosition);
      if (!hit) {
        this.handPosition.set(0, this.tableSurfaceY + 0.08, this.tableSurfaceZ);
      }
      this.handPosition.y = Math.max(this.handPosition.y, this.tableSurfaceY + 0.04);
    }

    // Update hand marker position and visibility
    if (this.handMarker) {
      if (frame.hasHand) {
        // Hide marker when close to a cake so it doesn't look like part of the cake
        let nearCake = false;
        for (const cake of this.cakes) {
          if (!cake.isThrown && cake.mesh.position.distanceTo(this.handPosition) < 0.35) {
            nearCake = true;
            break;
          }
        }

        this.handMarker.visible = !nearCake;
        this.handMarker.position.copy(this.handPosition);
        
        // Change color based on pinch state
        const mat = this.handMarker.material as MeshStandardMaterial;
        if (frame.pinch) {
          mat.color.setHex(0x00ff88);
          mat.emissive.setHex(0x00ff88);
          mat.opacity = 0.9;
          this.handMarker.scale.setScalar(1.15);
        } else {
          mat.color.setHex(0x00ff88);
          mat.emissive.setHex(0x00ff88);
          mat.opacity = 0.6;
          this.handMarker.scale.setScalar(1.0);
        }
      } else {
        this.handMarker.visible = false;
      }
    }

    // Handle pinch to grab/throw cakes, unless a pointer drag already owns one
    if (this.gameState === "playing" && frame.hasHand && !this.pointerActive) {
      // Pinch just started - try to grab a cake on the table
      if (frame.pinch && !this.wasHandPinching && !this.heldCake) {
        for (const cake of this.cakes) {
          if (!cake.isHeld && !cake.isThrown && cake.mesh.position.distanceTo(this.handPosition) < 0.5) {
            cake.isHeld = true;
            cake.nearMouthTime = 0;
            this.heldCake = cake;
            break;
          }
        }
      }

      // While pinching - move held cake
      if (frame.pinch && this.heldCake) {
        this.heldCake.mesh.position.lerp(this.handPosition, 0.6);
        this.heldCake.velocity.set(0, 0, 0);
      }

      // Pinch released - throw cake
      if (!frame.pinch && this.wasHandPinching && this.heldCake) {
        this.throwCake(this.heldCake);
      }
    }

    this.wasHandPinching = frame.pinch;
  }

  private triggerScreenFlash() {
    if (!this.flashOverlay) return;
    this.flashAnimation?.cancel();

    if (!this.flashOverlay.animate) {
      this.flashOverlay.style.opacity = '1';
      setTimeout(() => {
        if (this.flashOverlay) this.flashOverlay.style.opacity = '0';
      }, 180);
      return;
    }

    this.flashAnimation = this.flashOverlay.animate(
      [
        { opacity: 0, transform: "scale(0.92)" },
        { opacity: 1, transform: "scale(1)" },
        { opacity: 0, transform: "scale(1.05)" }
      ],
      { duration: 240, easing: "cubic-bezier(0.2, 0.7, 0.2, 1)" }
    );

    this.flashAnimation.onfinish = () => {
      if (this.flashOverlay) this.flashOverlay.style.opacity = "0";
    };
  }

  private triggerScreenShake() {
    const target = document.body;
    if (!target) return;

    this.screenShakeAnimation?.cancel();

    if (!target.animate) {
      const canvas = this.renderer.domElement;
      const originalTransform = canvas.style.transform;
      let shakeCount = 0;
      const shakeInterval = setInterval(() => {
        if (shakeCount > 8) {
          clearInterval(shakeInterval);
          canvas.style.transform = originalTransform;
          return;
        }
        const x = (Math.random() - 0.5) * 12;
        const y = (Math.random() - 0.5) * 12;
        canvas.style.transform = `translate(${x}px, ${y}px)`;
        shakeCount++;
      }, 30);
      return;
    }

    target.style.transformOrigin = "50% 50%";
    const steps = 12;
    const intensity = 14;
    const rotation = 1.2;
    const keyframes: Keyframe[] = [];

    for (let i = 0; i <= steps; i++) {
      const progress = i / steps;
      const damping = 1 - progress;
      const x = (Math.random() - 0.5) * intensity * damping;
      const y = (Math.random() - 0.5) * intensity * damping;
      const r = (Math.random() - 0.5) * rotation * damping;
      keyframes.push({ transform: `translate(${x}px, ${y}px) rotate(${r}deg)` });
    }

    keyframes[0] = { transform: "translate(0px, 0px) rotate(0deg)" };
    keyframes[keyframes.length - 1] = { transform: "translate(0px, 0px) rotate(0deg)" };

    this.screenShakeAnimation = target.animate(keyframes, {
      duration: 380,
      easing: "ease-out"
    });
  }

  private showFloatingEmoji(screenX: number, screenY: number, delay: number = 0, scale: number = 1) {
    const emojis = ["YUM!", "🎉", "💕", "✨", "😋", "🥳", "💖"];
    const emoji = emojis[Math.floor(Math.random() * emojis.length)];
    const driftX = (Math.random() - 0.5) * 60;
    const driftY = (Math.random() - 0.5) * 20;
    const tilt = (Math.random() - 0.5) * 24;
    const size = Math.round(62 * scale);

    const el = document.createElement("div");
    el.textContent = emoji;
    el.style.cssText = `
      position: fixed;
      left: ${screenX + driftX}px;
      top: ${screenY + driftY}px;
      font-size: ${size}px;
      font-weight: 700;
      pointer-events: none;
      z-index: 1000;
      opacity: 0;
      text-shadow: 0 0 16px rgba(255, 236, 140, 0.9), 0 0 36px rgba(255, 185, 80, 0.7);
      animation: floatUp 1.1s cubic-bezier(0.21, 0.61, 0.35, 1) forwards;
      animation-delay: ${delay}ms;
    `;
    el.style.setProperty("--emoji-tilt", `${tilt}deg`);
    el.style.setProperty("--emoji-tilt-end", `${-tilt * 0.35}deg`);
    document.body.appendChild(el);

    const styleId = "floatUpStyle";
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }
    style.textContent = `
      @keyframes floatUp {
        0% { transform: translate(-50%, 0) scale(0.4) rotate(var(--emoji-tilt)); opacity: 0; }
        15% { opacity: 1; }
        60% { transform: translate(-50%, -90px) scale(1.2) rotate(var(--emoji-tilt-end)); opacity: 1; }
        100% { transform: translate(-50%, -170px) scale(1) rotate(0deg); opacity: 0; }
      }
    `;

    setTimeout(() => el.remove(), 1200 + delay);
  }

  private createStarBurst(position: Vector3, count: number = 12) {
    // Create BIGGER star-shaped particles bursting outward
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const size = 0.11 + Math.random() * 0.09;
      const starGeo = new OctahedronGeometry(size);
      const hue = 0.12 + Math.random() * 0.05;
      const starMat = new MeshStandardMaterial({
        color: new Color().setHSL(hue, 0.95, 0.65),
        emissive: new Color(0xffb400),
        emissiveIntensity: 1.5,
        roughness: 0.25,
        metalness: 0.2
      });
      const star = new Mesh(starGeo, starMat);
      star.position.copy(position);
      star.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, angle);

      const speed = 3.6 + Math.random() * 3.8;
      const velocity = new Vector3(
        Math.cos(angle) * speed,
        Math.sin(angle) * speed + 2.6,
        (Math.random() - 0.5) * 4
      );

      this.scene.add(star);
      this.particles.push({ mesh: star, velocity, life: 1.4 + Math.random() * 0.5 });
    }
  }

  private createHeartBurst(position: Vector3, count: number = 8) {
    // Create BIGGER heart-shaped particles floating up
    for (let i = 0; i < count; i++) {
      const heartGeo = new SphereGeometry(0.14, 12, 12);
      const heartMat = new MeshStandardMaterial({
        color: new Color(0xff72bc),
        emissive: new Color(0xff2f8a),
        emissiveIntensity: 1.1,
        roughness: 0.35
      });
      const heart = new Mesh(heartGeo, heartMat);
      heart.position.copy(position);
      heart.position.x += (Math.random() - 0.5) * 0.5;
      heart.position.z += (Math.random() - 0.5) * 0.5;
      heart.scale.set(1.1, 1.5, 0.7);

      const angle = Math.random() * Math.PI * 2;
      const upSpeed = 5.5 + Math.random() * 2.5;
      const velocity = new Vector3(
        Math.cos(angle) * 1.1,
        upSpeed,
        Math.sin(angle) * 1.1
      );

      this.scene.add(heart);
      this.particles.push({ mesh: heart, velocity, life: 1.7 + Math.random() * 0.6 });
    }
  }

  private createSparkleRing(position: Vector3) {
    // Create MULTIPLE expanding rings for more impact
    for (let r = 0; r < 3; r++) {
      setTimeout(() => {
        const ringGeo = new RingGeometry(0.22, 0.3, 48);
        const ringMat = new MeshStandardMaterial({
          color: new Color(0xffd36a),
          emissive: new Color(0xffb347),
          emissiveIntensity: 1.8,
          transparent: true,
          opacity: 0.9,
          side: DoubleSide
        });
        const ring = new Mesh(ringGeo, ringMat);
        ring.position.copy(position);
        ring.rotation.x = Math.PI / 2;
        ring.rotation.z = Math.random() * Math.PI;

        this.scene.add(ring);
        this.sparkleRings.push({ mesh: ring, life: 0.7, maxLife: 0.7 });
      }, r * 90);
    }
  }

  private updateSparkleRings(dt: number) {
    for (let i = this.sparkleRings.length - 1; i >= 0; i--) {
      const ring = this.sparkleRings[i];
      ring.life -= dt;

      if (ring.life <= 0) {
        this.scene.remove(ring.mesh);
        ring.mesh.geometry.dispose();
        (ring.mesh.material as MeshStandardMaterial).dispose();
        this.sparkleRings.splice(i, 1);
        continue;
      }

      // Expand the ring MORE
      const progress = 1 - (ring.life / ring.maxLife);
      const scale = 0.7 + progress * 10.5;
      ring.mesh.scale.setScalar(scale);

      // Fade out
      (ring.mesh.material as MeshStandardMaterial).opacity = (ring.life / ring.maxLife) * 0.85;
    }
  }

  private updateEatingCakes(dt: number) {
    for (let i = this.eatingCakes.length - 1; i >= 0; i--) {
      const eating = this.eatingCakes[i];
      // Speed of eating animation (fast + snappy so it feels like a "slurp")
      eating.progress += dt * 3.6;

      if (eating.progress >= 1) {
        // Animation complete - remove cake (from whatever parent it has)
        eating.mesh.visible = false;
        eating.mesh.parent?.remove(eating.mesh);
        this.scene.remove(eating.mesh);
        eating.mesh.traverse((child) => {
          if (child instanceof Mesh) {
            child.geometry?.dispose();
            if (child.material) {
              if (Array.isArray(child.material)) {
                child.material.forEach(m => m.dispose());
              } else {
                child.material.dispose();
              }
            }
          }
        });
        this.eatingCakes.splice(i, 1);
        continue;
      }

      // Animate cake shrinking into mouth (spiral + spin so it doesn't look stuck)
      const t = MathUtils.clamp(eating.progress, 0, 1);
      const easeT = 1 - Math.pow(1 - t, 4); // stronger ease into the mouth

      // Base movement
      const basePos = new Vector3().lerpVectors(eating.startPos, eating.targetPos, easeT);

      // Add a tightening spiral around the path
      const spiralRadius = (1 - easeT) * 0.18;
      const spiralAngle = t * Math.PI * 12;
      basePos.x += Math.cos(spiralAngle) * spiralRadius;
      basePos.y += Math.sin(spiralAngle) * spiralRadius * 0.85;
      basePos.z += Math.sin(spiralAngle * 0.6) * spiralRadius * 0.25;
      eating.mesh.position.copy(basePos);

      // Shrink with a bit of lingering volume so it's readable
      const shrinkScale = Math.max(0.0005, Math.pow(1 - easeT, 2.1));
      eating.mesh.scale.setScalar(shrinkScale);

      // Spin faster as it gets eaten
      const spin = 14 + easeT * 45;
      eating.mesh.rotation.y += dt * spin;
      eating.mesh.rotation.x += dt * (spin * 0.9);
      eating.mesh.rotation.z += dt * (spin * 0.6);

      // Fade out near the end for extra cuteness (avoid harsh pop)
      if (t > 0.6) {
        const fade = MathUtils.clamp((1 - t) / 0.4, 0, 1);
        eating.mesh.traverse((child) => {
          if (child instanceof Mesh) {
            const mat = child.material;
            if (Array.isArray(mat)) {
              mat.forEach((m) => {
                (m as any).transparent = true;
                (m as any).opacity = fade;
              });
            } else {
              (mat as any).transparent = true;
              (mat as any).opacity = fade;
            }
          }
        });
      }
    }
  }

  private feedBabyWithCake(cake: CakeObject, mouthPos: Vector3) {
    // Score and meter updates
    this.score += 10;
    this.cryMeter = Math.max(0, this.cryMeter - 0.2);
    this.successSound?.play();
    if (this.heldCake === cake) {
      this.heldCake = undefined;
    }
    cake.isHeld = false;
    cake.isThrown = false;

    // === VISUAL EFFECTS ===

    // 1. Screen flash + shake for IMPACT!
    this.triggerScreenFlash();
    this.triggerScreenShake();

    // 2. Get screen position for floating emoji
    const screenPos = cake.mesh.position.clone().project(this.camera);
    const screenX = (screenPos.x * 0.5 + 0.5) * window.innerWidth;
    const screenY = (-screenPos.y * 0.5 + 0.5) * window.innerHeight;

    // 3. Show floating emoji/text
    this.showFloatingEmoji(screenX, screenY, 0, 1.1);
    this.showFloatingEmoji(screenX - 60, screenY + 20, 120, 0.95);
    this.showFloatingEmoji(screenX + 60, screenY + 20, 220, 1.0);

    // 4. Star burst at cake position (bigger)
    this.createStarBurst(cake.mesh.position.clone(), 20);

    // 5. Hearts floating up (more)
    this.createHeartBurst(mouthPos.clone(), 10);

    // 6. Sparkle rings expanding from mouth
    this.createSparkleRing(mouthPos.clone());

    // 7. Confetti (gold, white, pink, green)
    this.createConfetti(cake.mesh.position, new Color(0xFFD700), 30);
    this.createConfetti(cake.mesh.position, new Color(0xFFFFFF), 24);
    this.createConfetti(cake.mesh.position, new Color(0xFF69B4), 20);
    this.createConfetti(cake.mesh.position, new Color(0x00FF88), 16);

    // 8. Start eating animation - cake gets sucked into mouth
    const insideMouth = (() => {
      if (!this.babyModel) return mouthPos.clone();
      const babyCenter = this.babyModel.position.clone().add(new Vector3(0, 0.55, 0));
      const into = babyCenter.sub(mouthPos).normalize();
      return mouthPos.clone().add(into.multiplyScalar(0.18));
    })();

    this.eatingCakes.push({
      mesh: cake.mesh,
      targetPos: insideMouth,
      startPos: cake.mesh.position.clone(),
      progress: 0
    });

    // 9. Baby happy reaction - SUPER dramatic!
    if (this.babyModel) {
      this.babyReactionTime = 0;
      this.babyReactionDuration = 0.85;
      this.babyReactionSeed = Math.random() * 2;
    }
  }

  private animate = () => {
    requestAnimationFrame(this.animate);
    
    const dt = Math.min(0.033, this.clock.getDelta());
    
    // Animate baby - bounce and mouth
    if (this.babyModel && this.babyBasePosition) {
      // Gentle bounce animation
      const bounce = Math.sin(Date.now() * 0.003) * 0.02;
      this.babyModel.position.set(
        this.babyBasePosition.x,
        this.babyBasePosition.y + bounce,
        this.babyBasePosition.z
      );

      let reactionScale = 1;
      let reactionShake = 0;
      if (this.babyReactionDuration > 0) {
        this.babyReactionTime += dt;
        const t = MathUtils.clamp(this.babyReactionTime / this.babyReactionDuration, 0, 1);
        const pop = Math.sin(Math.min(t * Math.PI, Math.PI));
        const bounceWobble = Math.sin((t + this.babyReactionSeed) * Math.PI * 6) * (1 - t) * 0.12;
        reactionScale = MathUtils.clamp(1 + 0.6 * pop + bounceWobble, 0.9, 1.7);
        reactionShake = Math.sin((t + this.babyReactionSeed) * Math.PI * 18) * (1 - t) * 0.18;
        if (t >= 1) {
          this.babyReactionDuration = 0;
          this.babyReactionTime = 0;
        }
      }
      this.babyModel.scale.setScalar(this.babyBaseScale * reactionScale);
      
      // Slight wobble when crying
      const wobble = this.gameState === "playing"
        ? Math.sin(Date.now() * 0.01) * 0.05 * this.cryMeter
        : 0;
      this.babyModel.rotation.z = wobble + reactionShake;

      this.babyModel.updateWorldMatrix(true, false);
    }
    
    // Mouth open/close cycle - animate the actual mesh!
    this.mouthTimer += dt * 3;
    this.mouthOpen = Math.sin(this.mouthTimer) > -0.1;
    
    if (this.mouthMesh) {
      const openScale = this.mouthOpen ? 1.5 : 0.8;
      this.mouthMesh.scale.y = openScale;
    }
    
    if (this.gameState === "playing") {
      this.updateHandTracking(dt);
      this.updateGame(dt);
    }

    this.updateParticles(dt);
    this.updateSparkleRings(dt);
    this.updateEatingCakes(dt);

    this.renderer.render(this.scene, this.camera);
  }

  private updateGame(dt: number) {
    // Spawn cakes
    this.spawnTimer += dt;
    if (this.spawnTimer > 2) {
      if (this.cakes.length < this.maxCakes) {
        this.spawnCake();
      }
      this.spawnTimer = 0;
    }
    
    // Update cakes
    const mouthPos = this.getMouthPosition();
    const mouthScreen = mouthPos.clone().project(this.camera);
    const mouthHitRadius = 0.4;
    const mouthCaptureRadius = 0.6;
    const mouthCaptureTime = 0.22;
    const mouthCaptureSpeed = 0.25;
    const mouthScreenRadius = 0.12;
    const mouthScreenTime = 0.1;
    for (let i = this.cakes.length - 1; i >= 0; i--) {
      const cake = this.cakes[i];
      const distToMouth = cake.mesh.position.distanceTo(mouthPos);

      if (cake.isThrown || cake.isHeld) {
        let screenNear = false;
        if (cake.isHeld) {
          const cakeScreen = cake.mesh.position.clone().project(this.camera);
          const dx = cakeScreen.x - mouthScreen.x;
          const dy = cakeScreen.y - mouthScreen.y;
          screenNear = Math.hypot(dx, dy) < mouthScreenRadius;
        }

        if (screenNear || distToMouth < mouthCaptureRadius) {
          cake.nearMouthTime += dt;
        } else {
          cake.nearMouthTime = 0;
        }

        const speed = cake.isHeld ? 0 : cake.velocity.length();
        // Only gate on the mouth cycle when the mouth is actually animated, so
        // an invisible timer can never reject an accurate throw.
        const mouthReady = !this.mouthMesh || this.mouthOpen;
        const shouldFeed =
          (distToMouth < mouthHitRadius && mouthReady) ||
          (cake.isHeld && screenNear && cake.nearMouthTime > mouthScreenTime) ||
          (distToMouth < mouthCaptureRadius && (speed < mouthCaptureSpeed || cake.nearMouthTime > mouthCaptureTime));

        if (shouldFeed) {
          this.feedBabyWithCake(cake, mouthPos);
          this.cakes.splice(i, 1);
          continue;
        }
      }
      
      if (!cake.isHeld) {
        // Cakes should rest on the table until thrown
        if (!cake.isThrown) {
          cake.velocity.set(0, 0, 0);
          cake.mesh.position.y = this.tableSurfaceY + this.cakeRestOffsetY;
          continue;
        }

        // Thrown cakes get physics
        cake.velocity.y -= 9.8 * dt;
        cake.mesh.position.add(cake.velocity.clone().multiplyScalar(dt));
        
        // Check if hit baby face/body (messy miss). A cake still closing on the
        // mouth is left alone: the mouth sits inside the body, so splattering
        // first would swallow every accurate throw.
        if (cake.isThrown && this.babyModel && distToMouth > mouthCaptureRadius) {
          const closingOnMouth = mouthPos.clone().sub(cake.mesh.position).dot(cake.velocity) > 0;
          const bodyCenter = this.getBodyCenter();
          if (!closingOnMouth && cake.mesh.position.distanceTo(bodyCenter) < this.bodyHitRadius) {
            // Messy splatter
            this.createSplatter(cake.mesh.position, new Color(0xf4a4b7), 12); // Pink frosting
            this.createSplatter(cake.mesh.position, new Color(0xf3c18b), 5);  // Cake crumbs

            this.scene.remove(cake.mesh);
            this.cakes.splice(i, 1);
            continue;
          }
        }
        
        // Remove if fallen
        if (cake.mesh.position.y < -1) {
          if (cake.isThrown) {
            this.cryMeter = Math.min(1, this.cryMeter + 0.1);
            this.missSound?.play();
          }
          this.scene.remove(cake.mesh);
          this.cakes.splice(i, 1);
        }
      }
    }
    
    // Increase cry meter over time
    this.cryMeter = Math.min(1, this.cryMeter + dt * 0.03);
    
    // Update UI
    document.getElementById('score')!.textContent = `Score: ${this.score}`;
    document.getElementById('cry-fill')!.style.width = `${this.cryMeter * 100}%`;
    
    // Game over check
    if (this.cryMeter >= 1) {
      this.gameOver();
    }
  }

  private gameOver() {
    this.gameState = "gameover";
    this.crySound?.stop();
    
    document.getElementById('score')!.style.display = 'none';
    document.getElementById('cry-meter-container')!.style.display = 'none';
    document.getElementById('gameover-screen')!.style.display = 'flex';
    document.getElementById('final-score')!.textContent = `Score: ${this.score}`;
    
    // Clear cakes
    for (const cake of this.cakes) {
      this.scene.remove(cake.mesh);
    }
    this.cakes = [];
  }
}

// ============================================
// CAKE OBJECT - Simple cake data class
// ============================================
class CakeObject {
  mesh: Object3D;
  velocity: Vector3 = new Vector3();
  isHeld: boolean = false;
  isThrown: boolean = false;
  nearMouthTime: number = 0;

  constructor(mesh: Object3D) {
    this.mesh = mesh;
  }
}

// ============================================
// AUTO-START GAME
// ============================================
document.addEventListener('DOMContentLoaded', () => {
  new GimmeMyCakeGame();
});
